/**
 * Installation. Cellvora is distributed from its own website and nowhere else:
 * you visit the site once, install it, and from then on it is an application
 * on the device with its own icon, its own window and no browser chrome.
 *
 * This module is the whole client side of that. It:
 *   - registers the service worker that makes the app installable and lets the
 *     shell open without a network round trip,
 *   - captures Chromium's `beforeinstallprompt` so the page can offer a real
 *     one-tap install instead of hiding it in a browser menu,
 *   - works out which set of instructions the person in front of us needs,
 *   - and notices when the app has already been installed.
 *
 * WHY A PWA RATHER THAN AN APP STORE BUILD
 * An agency's staff arrive on whatever they own — an iPhone, a Pixel, a work
 * Windows laptop, a personal Mac. A store release means separate review queues
 * and separate update cycles per platform, and a security fix would ship when
 * the slowest reviewer allowed it. Installed from the web, everyone is on the
 * same build the moment it is deployed, which for software holding other
 * people's dates of birth and bank details is the safer property. The native
 * shells (Tauri on the desktop, Expo on mobile) load this same application.
 */

export type Platform = 'ios' | 'android' | 'desktop' | 'installed';

/** Chromium fires this so a page can defer the install prompt to its own UI. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function announce(): void {
  for (const fn of listeners) {
    try { fn(); } catch { /* a broken listener must not break installation */ }
  }
}

export function onInstallChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * True when we are running as the installed app rather than in a browser tab.
 *
 * WHY `fullscreen` IS NOT ON THIS LIST
 * It used to be, and it was wrong. `display-mode: fullscreen` matches whenever
 * the browsing context is displayed fullscreen — which includes an ordinary
 * browser tab in a window somebody put into fullscreen, on a desktop with F11
 * or on a phone browser in immersive mode. Nothing has been installed in that
 * case, but this returned true, so the install page greeted them with
 * "Cellvora is installed", disabled its own button, and replaced the
 * instructions with "nothing else to do here". They were left looking at a
 * website being told it was an app, with no way to make it one.
 *
 * Losing `fullscreen` costs nothing: the manifest declares `display:
 * standalone` with an override chain of window-controls-overlay, standalone,
 * minimal-ui, so the real installed app reports one of the three below and
 * never `fullscreen`. Each of those only ever matches an installed app; a
 * browser tab reports `browser`.
 */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mm = window.matchMedia?.('(display-mode: standalone)').matches
    || window.matchMedia?.('(display-mode: window-controls-overlay)').matches
    || window.matchMedia?.('(display-mode: minimal-ui)').matches;
  // iOS Safari predates display-mode and reports this instead.
  const ios = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return Boolean(mm || ios);
}

export function detectPlatform(): Platform {
  if (isStandalone() || installed) return 'installed';
  const ua = navigator.userAgent;
  const touchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1; // iPadOS 13+
  if (/iPhone|iPad|iPod/i.test(ua) || touchMac) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

export function installState(): { platform: Platform; canPrompt: boolean } {
  return { platform: detectPlatform(), canPrompt: deferred !== null };
}

/**
 * Shows the browser's own install dialog when one is available. Returns what
 * happened so the caller can leave the button honest — iOS has no programmatic
 * install at all, which is why the page always shows written steps too.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  const ev = deferred;
  deferred = null;            // a captured prompt may only be used once
  announce();
  await ev.prompt();
  const { outcome } = await ev.userChoice;
  if (outcome === 'accepted') { installed = true; announce(); }
  return outcome;
}

/** Called once from main.ts, before the first render. */
export function initInstall(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();       // stop Chrome's mini-infobar; we have our own button
    deferred = e as BeforeInstallPromptEvent;
    announce();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    announce();
  });

  // The service worker is what makes the app installable and what lets the
  // shell paint before the network answers. It is registered after load so it
  // never competes with the first render for bandwidth.
  const secure = location.protocol === 'https:' || location.hostname === 'localhost';
  if ('serviceWorker' in navigator && secure) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
        // Registration is refused in some private-browsing modes. The
        // application still works; it just is not installable there.
      });
    });
  }
}
